/**
 * Boundary Query Hooks
 *
 * React Query hooks for fetching area boundary data.
 */

import { useQuery } from '@zg-rentals/react-query5';
import type { UseQueryOptions } from '@zg-rentals/react-query5';
import { area, getAreaScopeKey, type AreaIdentity, type EncodedBoundaryResponse } from '@/src/lib/api/endpoints/area';

// Query keys for type-safe cache management
export const boundaryKeys = {
  all: ['boundary'] as const,
  byIdentity: (identity: AreaIdentity) => [...boundaryKeys.all, 'byIdentity', getAreaScopeKey(identity)] as const,
};

/**
 * Hook for fetching encoded boundary data by area ID
 *
 * @param areaId - Area ID to fetch boundary for
 * @param options - React Query options
 * @returns Query result with boundary data
 *
 * @example
 * ```tsx
 * const { data: boundaryData } = useEncodedBoundary(areaIdentity);
 * ```
 */
export const useEncodedBoundary = (
  identity: AreaIdentity | undefined,
  options?: Omit<UseQueryOptions<EncodedBoundaryResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: identity ? boundaryKeys.byIdentity(identity) : (['boundary', 'disabled'] as const),
    queryFn: async () => {
      if (!identity) {
        throw new Error('Area identity is required for boundary fetch');
      }
      return await area.getEncodedBoundary(identity);
    },
    enabled: !!identity && (!!identity.scope || !!identity.legacyAreaId || !!identity.id),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // Retry on failure to handle transient network errors
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    meta: {
      tags: ['boundary'],
    },
    ...options,
  });
};
